import { useEffect, useLayoutEffect, useRef, useSyncExternalStore, type MouseEvent, type ReactElement, type RefObject } from "react";
import { COMPASS, HUD, MINIMAP_NAMES_MOST, MINIMAP_OTHER_LOOKS, MinimapOtherKind, chooseOtherNames, createMinimapNames, minimapKindsPresent, minimapOffset, minimapOthersSignature, type MinimapMark, type MinimapOthers, type MinimapPlaceSpot, type MinimapSpot, type Size } from "odyssey-shared";
import { HoverWords } from "../bar/hover-words.tsx";
import { Glyph, GlyphButton, HudFrame, logical } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import { slotRect } from "../hud/area-slots.ts";
import type { HudArea } from "../hud/slots.ts";
import type { BesideField, MapPictureSource } from "../map-picture/pictures.ts";
import { COMPASS_CANVAS } from "./compass.ts";
import { CompassBand } from "./compass-band.tsx";
import { groundPhase, paintField, swatchOf } from "./paint.ts";
import { keepsFieldFor, newFieldHold } from "./field-hold.ts";
import { bindPlaceLine } from "./place-line.ts";
import type { MinimapField, MinimapSnapshot, MinimapSource } from "./store.ts";

/**
 * The minimap on the HUD (P1.9-T52, R-0055; ADR-061 decision 1; the design's `minimap` card, a visual reference): a DOM
 * frame in the minimap slot holding a canvas of the area within the player's vision, with the zoom in, zoom out and Map
 * controls in a column to its left. The Map button opens and shuts the Map window as M does (P2.12-T22, ADR-100
 * decisions 43 and 44); the field larger, with the marks' names and the legend, is the Map's Nearby level
 * (`world-map/window.tsx`), which draws with this file's `FieldCanvas`, `Labels`, `OtherNames` and `Legend`. The canvas is painted from the store's marks and the
 * player's position, which only the painter hears each frame (`onMove`): React draws again when a value it shows
 * changes. The ground is drawn across the whole field; no mark outside vision is (`minimapMarks`), and the frame takes no pointer events but its controls.
 *
 * The frame is the compass's 90 square (P1.9-T187, ADR-061's amendment of 2026-10-14): the field's 70 by 70 canvas in
 * the middle of a 7 pixel band of the compass's letters and ticks (`compass.ts`), the band's canvas under it.
 *
 * Under the field stands one line that says where the player is, "Willowmere: 120, 88" (`PlaceReadout`, P1.9-T197),
 * unless Settings turns it off.
 *
 * The Map button (the `map` glyph the expand control wore) sits under the two zoom controls when the page can open the
 * map, and is lit while the Map is open.
 *
 * Every control is a button: the pointer, a touch and the keyboard (Enter and Space on a focused one) all reach it.
 */

/**
 * The Map button's two parts: the Map's open state, which lights it, and what a press does, which is to open the Map
 * if it is shut and shut it if it is open.
 */
export interface MapButton {
  readonly source: { readonly subscribe: (listener: () => void) => () => void; readonly getSnapshot: () => { readonly open: boolean; readonly presentation?: string | undefined } };
  readonly toggle: () => void;
  readonly openAt?: ((point: { readonly x: number; readonly y: number }) => void) | undefined;
}

/**
 * What the minimap acts on: its source, the player's position (read, never copied) the canvas paints the ground from
 * and the changes the controls ask for. `ground` gives the map's pictures once the map is booted (P1.9-T178), null
 * before and where a page has none, and the fields then draw flat ground. `others` is the buffer of other players and
 * creatures in vision the scene refills each frame (P1.9-T179), read by the painters when they are told and by no one
 * else; a page without it draws none. `mapButton` is given once the world map opens, and the minimap shows its Map
 * button only then. `onMapField` asks the store for the Map window's Nearby field at a size, or says it is no longer
 * wanted (P2.12-T21).
 */
export interface HudMinimap {
  readonly source: MinimapSource;
  readonly position: { readonly x: number; readonly y: number };
  readonly onMove: (listener: () => void) => () => void;
  readonly ground?: (() => MapPictureSource | null) | undefined;
  readonly others?: MinimapOthers | undefined;
  readonly pin?: (() => { readonly x: number; readonly y: number } | null) | undefined;
  readonly party?: (() => readonly MinimapSpot[]) | undefined;
  readonly places?: (() => readonly MinimapPlaceSpot[]) | undefined;
  readonly placeCount?: (() => number) | undefined;
  readonly onZoomIn: () => void;
  readonly onZoomOut: () => void;
  readonly mapButton?: MapButton | undefined;
  readonly onMapField?: ((size: Size | null) => void) | undefined;
}

/**
 * The controls' sizes in logical pixels: the zoom and Map buttons' size, the gap between them and how far down the
 * frame's top they start.
 */
export const MINIMAP_LAYOUT = { controlSize: 12, controlGap: 2, controlTop: 18 } as const;

/**
 * Keeps a pointer press on a control from taking focus, so the game keeps its keys.
 *
 * @param event The press.
 */
function keepFocus(event: MouseEvent<HTMLElement>): void {
  event.preventDefault();
}

/**
 * A field's canvas: painted when the field changes, and again as the player moves a canvas pixel, or a chunk of the
 * map's picture lands. With the map's pictures it copies the vision rectangle's part of the picture its slot holds;
 * without them it draws the flat ground, whose grid moves with the position. A slot's picture is freed when the canvas
 * goes, so a Map that is shut or at Region holds none.
 *
 * @param props The `field` to paint, its picture `slot`, and the minimap's `position`, `onMove`, `ground` and `others`.
 * @returns The canvas.
 */
export function FieldCanvas({ field, slot, position, onMove, ground, others, label }: { readonly field: MinimapField; readonly slot: string; readonly position: HudMinimap["position"]; readonly onMove: HudMinimap["onMove"]; readonly ground: HudMinimap["ground"]; readonly others: HudMinimap["others"]; readonly label: string }): ReactElement {
  const canvas = useRef<HTMLCanvasElement>(null);
  const besides = useRef<BesideField[]>([]);
  const held = useRef(newFieldHold());
  useLayoutEffect(() => {
    const context = canvas.current?.getContext("2d") ?? null;
    if (context === null) {
      return undefined;
    }
    const scale = field.scale;
    let shownX = Number.NaN;
    let shownY = Number.NaN;
    let shownVersion = -2;
    let shownOthers = 0;
    /**
     * Paints the field when the player has moved a canvas pixel, the picture has changed, a mark of another player or
     * creature has moved a canvas pixel, or a paint is forced; never, after a crossing's change of stage, while the new
     * map's picture still owes chunks and the last drawing had ground (`keepsFieldFor`), for at most `BARE_HOLD_PAINTS` paints.
     *
     * @param force Paint even when nothing the field shows has changed.
     */
    const redraw = (force: boolean): void => {
      const { x, y } = position;
      const source = ground?.() ?? null;
      const picture = source?.view(slot, scale, x, y, field.size.width, field.size.height) ?? null;
      if (keepsFieldFor(held.current, source, picture, slot)) {
        return;
      }
      const besideCount = source?.beside?.views(slot, scale, x, y, field.size.width, field.size.height, besides.current) ?? 0;
      let besideVersion = 0;
      for (let i = 0; i < besideCount; i += 1) {
        besideVersion += (besides.current[i]?.ground?.version ?? 0) * (i + 1);
      }
      const phaseX = picture === null ? (scale >= 2 ? groundPhase(x, scale) : 0) : minimapOffset(x, scale);
      const phaseY = picture === null ? (scale >= 2 ? groundPhase(y, scale) : 0) : minimapOffset(y, scale);
      const version = (picture?.version ?? -1) + besideVersion * 65_536;
      const marked = others === undefined ? 0 : minimapOthersSignature(others, x, y, field.size.width, field.size.height, scale);
      if (!force && phaseX === shownX && phaseY === shownY && version === shownVersion && marked === shownOthers) {
        return;
      }
      shownX = phaseX;
      shownY = phaseY;
      shownVersion = version;
      shownOthers = marked;
      paintField(context, field.size, scale, x, y, field.marks, picture, others ?? null, besides.current, besideCount);
    };
    redraw(true);
    return onMove(() => redraw(false));
  }, [field, slot, position, onMove, ground, others]);
  useEffect(
    () => () => {
      const source = ground?.() ?? null;
      source?.release(slot);
      source?.beside?.release(slot);
    },
    [slot, ground],
  );
  return <canvas ref={canvas} className="ot-minimap__canvas" data-minimap="canvas" role="img" aria-label={label} width={field.size.width} height={field.size.height} style={{ width: logical(field.size.width), height: logical(field.size.height) }} />;
}

/**
 * The zoom in and zoom out buttons, disabled at the ends of the steps.
 *
 * @param props The `snapshot`, the button `size` and what the buttons ask (`minimap`).
 * @returns The two buttons.
 */
function ZoomButtons({ snapshot, size, minimap }: { readonly snapshot: MinimapSnapshot; readonly size: number; readonly minimap: HudMinimap }): ReactElement {
  return (
    <>
      <span onMouseDown={keepFocus}>
        <GlyphButton glyph="plus" label={say("minimap.zoomIn")} size={size} disabled={!snapshot.canZoomIn} onClick={minimap.onZoomIn} data={{ "data-minimap": "zoom-in" }} />
      </span>
      <span onMouseDown={keepFocus}>
        <GlyphButton glyph="minus" label={say("minimap.zoomOut")} size={size} disabled={!snapshot.canZoomOut} onClick={minimap.onZoomOut} data={{ "data-minimap": "zoom-out" }} />
      </span>
    </>
  );
}

/**
 * The names beside the Nearby field's party marks (the player has none, their dot is named in the legend): right of a mark, or left of it in the field's right 45 percent, so none
 * is clipped.
 *
 * @param props The `field`.
 * @returns The labels.
 */
export function Labels({ field }: { readonly field: MinimapField }): ReactElement {
  return (
    <>
      {field.marks.filter((mark: MinimapMark) => mark.kind === "party").map((mark: MinimapMark) => {
        const right = mark.left + mark.size / 2 > field.size.width * 0.55;
        const style = right ? { right: logical(field.size.width - mark.left + 1), top: logical(mark.top - 1) } : { left: logical(mark.left + mark.size + 2), top: logical(mark.top - 1) };
        return (
          <span key={`${mark.kind}-${mark.handle}`} className="ot-minimap__label ot-pixel-small" data-minimap="label" aria-hidden="true" style={style}>
            {mark.name}
          </span>
        );
      })}
    </>
  );
}

/**
 * The names of the other players in the Nearby field (P1.9-T179): a fixed pool of `MINIMAP_NAMES_MOST` labels the
 * painter's own listener moves, so a name follows its player without React drawing. When the marks shown change it
 * chooses the names (`chooseOtherNames`: players only, the nearer keeping its name where two would meet), and writes
 * to a label only what differs from what it holds.
 *
 * @param props The `field`, and the minimap's `position`, `onMove` and `others`.
 * @returns The labels, hidden until named.
 */
export function OtherNames({ field, position, onMove, others }: { readonly field: MinimapField; readonly position: HudMinimap["position"]; readonly onMove: HudMinimap["onMove"]; readonly others: HudMinimap["others"] }): ReactElement {
  const spans = useRef<(HTMLSpanElement | null)[]>([]);
  useLayoutEffect(() => {
    if (others === undefined) {
      return undefined;
    }
    const names = createMinimapNames();
    const held = new Array<{ name: unknown; anchor: number; top: number; flipped: number } | null>(MINIMAP_NAMES_MOST).fill(null);
    let shown = Number.NaN;
    /**
     * Writes the names for the marks held now, when they differ from the names shown.
     */
    const place = (): void => {
      const { x, y } = position;
      const signature = minimapOthersSignature(others, x, y, field.size.width, field.size.height, field.scale);
      if (signature === shown) {
        return;
      }
      shown = signature;
      chooseOtherNames(names, others, x, y, field.size.width, field.size.height, field.scale);
      for (let i = 0; i < MINIMAP_NAMES_MOST; i += 1) {
        const span = spans.current[i];
        if (span === null || span === undefined) {
          continue;
        }
        if (i >= names.count) {
          if (held[i] !== null) {
            span.style.display = "none";
            held[i] = null;
          }
          continue;
        }
        const entry = names.index[i] as number;
        const name = others.name[entry];
        const anchor = names.anchor[i] as number;
        const top = names.top[i] as number;
        const flipped = names.flipped[i] as number;
        const last = held[i] ?? null;
        if (last === null) {
          span.style.display = "";
        }
        if (last === null || last.name !== name) {
          span.textContent = name === undefined ? "" : name.family === "" ? name.first : `${name.first} ${name.family}`;
        }
        if (last === null || last.anchor !== anchor || last.flipped !== flipped) {
          span.style.left = flipped === 1 ? "" : logical(anchor);
          span.style.right = flipped === 1 ? logical(field.size.width - anchor) : "";
        }
        if (last === null || last.top !== top) {
          span.style.top = logical(top);
        }
        held[i] = { name, anchor, top, flipped };
      }
    };
    place();
    return onMove(place);
  }, [field, position, onMove, others]);
  return (
    <>
      {Array.from({ length: MINIMAP_NAMES_MOST }, (_, i) => (
        <span key={i} ref={(element) => { spans.current[i] = element; }} className="ot-minimap__label ot-pixel-small" data-minimap="other-name" aria-hidden="true" style={{ display: "none" }} />
      ))}
    </>
  );
}

/**
 * The legend's entries for other marks, in the order they are listed: the word, and the kinds it stands for.
 */
const LEGEND_KINDS = [
  { word: "minimap.players", kinds: [MinimapOtherKind.Player] },
  { word: "minimap.flagged", kinds: [MinimapOtherKind.FlaggedPlayer] },
  { word: "minimap.npcs", kinds: [MinimapOtherKind.Npc] },
  { word: "minimap.passive", kinds: [MinimapOtherKind.Passive] },
  { word: "minimap.quest", kinds: [MinimapOtherKind.Quest] },
  { word: "minimap.questTarget", kinds: [MinimapOtherKind.QuestTarget] },
  { word: "minimap.neutral", kinds: [MinimapOtherKind.Neutral] },
  { word: "minimap.hostile", kinds: [MinimapOtherKind.Aggressive] },
  { word: "minimap.elite", kinds: [MinimapOtherKind.Elite, MinimapOtherKind.Boss] },
] as const;

/**
 * Which legend entries a set of kinds present asks for: an entry is listed when any kind it stands for is present.
 *
 * @param present The kinds present, as a bit set (`minimapKindsPresent`).
 * @returns The entries' indexes in `LEGEND_KINDS`, in order.
 */
export function legendEntriesFor(present: number): number[] {
  const listed: number[] = [];
  LEGEND_KINDS.forEach((entry, index) => {
    if (entry.kinds.some((kind) => (present & (1 << kind)) !== 0)) {
      listed.push(index);
    }
  });
  return listed;
}

/**
 * A legend swatch: the pixels of a mark as the painter draws them, in an SVG of whole logical pixels.
 *
 * @param props The `mark` it shows (`swatchOf`).
 * @returns The swatch.
 */
function Swatch({ mark }: { readonly mark: Parameters<typeof swatchOf>[0] }): ReactElement {
  const swatch = swatchOf(mark);
  return (
    <span className="ot-minimap__swatch" aria-hidden="true">
      <svg viewBox={`0 0 ${swatch.width} ${swatch.height}`} shapeRendering="crispEdges" style={{ width: logical(swatch.width), height: logical(swatch.height) }}>
        {swatch.rects.map((rect, i) => (
          <rect key={i} x={rect.x} y={rect.y} width={rect.w} height={rect.h} fill={rect.fill} />
        ))}
      </svg>
    </span>
  );
}

/**
 * The kinds of other marks in view, as a bit set the legend reads (`minimapKindsPresent`), changing only when the set of
 * kinds does, so React renders then and not on a frame that moves a mark.
 *
 * @param others The minimap's `others`.
 * @param onMove How to hear a frame.
 * @returns The bit set.
 */
function useKindsPresent(others: HudMinimap["others"], onMove: HudMinimap["onMove"]): number {
  const read = (): number => (others === undefined ? 0 : minimapKindsPresent(others));
  return useSyncExternalStore(onMove, read, read);
}

/**
 * The legend's entries for the kinds of other marks in view (P1.9-T179): a swatch of the mark in the kind's colours or
 * its glyph, and its word, only for a kind present now.
 *
 * @param props The kinds `present` (`minimapKindsPresent`).
 * @returns The entries.
 */
function LegendKinds({ present }: { readonly present: number }): ReactElement {
  return (
    <>
      {legendEntriesFor(present).map((index) => {
        const entry = LEGEND_KINDS[index] as (typeof LEGEND_KINDS)[number];
        const look = MINIMAP_OTHER_LOOKS[entry.kinds[0]] as (typeof MINIMAP_OTHER_LOOKS)[number];
        return (
          <li key={entry.word} className="ot-minimap__legend-item" data-minimap="legend-kind" data-kind={entry.kinds[0]}>
            {look.shape === "dot" ? <Swatch mark={entry.kinds[0]} /> : <span className="ot-minimap__glyph" aria-hidden="true" style={{ color: `var(--ot-${look.token})` }}><Glyph name={look.shape} /></span>}
            {say(entry.word)}
          </li>
        );
      })}
    </>
  );
}

/**
 * The legend of what a mark can be (P1.9-T179): the player, the party only while the player has one (P1.9-T195), then
 * each kind of other mark in view. The Map window draws it at its Nearby level.
 *
 * @param props The minimap's `source`, `others` and `onMove`, and a `className` to add to the list.
 * @returns The legend.
 */
export function Legend({ source, others, onMove, className, listRef }: { readonly source: MinimapSource; readonly others: HudMinimap["others"]; readonly onMove: HudMinimap["onMove"]; readonly className?: string; readonly listRef?: RefObject<HTMLUListElement | null> }): ReactElement {
  const present = useKindsPresent(others, onMove);
  const inParty = useSyncExternalStore(source.subscribe, () => source.getSnapshot().party, () => source.getSnapshot().party);
  return (
    <ul ref={listRef} className={className === undefined ? "ot-minimap__legend" : `ot-minimap__legend ${className}`} data-minimap="legend">
      <li className="ot-minimap__legend-item">
        <Swatch mark="self" />
        {say("minimap.you")}
      </li>
      {inParty ? (
        <li className="ot-minimap__legend-item">
          <Swatch mark="party" />
          {say("minimap.party")}
        </li>
      ) : null}
      <LegendKinds present={present} />
    </ul>
  );
}

/**
 * The words of every entry the legend can list, which the Map window sizes the legend's strip from.
 *
 * @returns The words, in the order they are listed.
 */
export function legendWords(): readonly string[] {
  return [say("minimap.you"), say("minimap.party"), ...LEGEND_KINDS.map((entry) => say(entry.word))];
}

/**
 * A Map's open state that never changes, for a minimap given no Map button.
 */
const NO_MAP = { subscribe: (): (() => void) => () => undefined, getSnapshot: (): { readonly open: boolean; readonly presentation?: string | undefined } => ({ open: false }) } as const;

/**
 * The strip under the minimap, in logical pixels: one line of the body face at 6 px high. It is exactly as wide as the
 * minimap's frame (the author, 2026-10-16: the strip "should only match the width of the map itself"), so its width is
 * the slot's and not a constant. The place line is its first item; later map or location items sit beside it.
 */
export const PLACE_LINE = { height: 9 } as const;

/**
 * The strip under the field (P1.9-T211, a dark backing exactly the width of the minimap's frame, a flex row) and its first item, the
 * line that says where the player is (P1.9-T197): the place's name and the tile, "Willowmere: 120,
 * 88", right under the minimap's slot, one gutter below it and ending at its right edge, in the top of the room the
 * reserved location slot keeps (so the line follows the minimap's slot wherever the compass puts its edge). The painter's own listener writes it, and only when the
 * tile or the name changes, so walking draws nothing in React. The name is cut short before the numbers are.
 *
 * @param props The `area` it is laid out on, the `place` name, and the minimap's `position` and `onMove`.
 * @returns The line.
 */
export function PlaceReadout({ area, place, position, onMove }: { readonly area: HudArea; readonly place: string; readonly position: HudMinimap["position"]; readonly onMove: HudMinimap["onMove"] }): ReactElement {
  const name = useRef<HTMLSpanElement>(null);
  const tile = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    return bindPlaceLine(place, position, onMove, name.current, tile.current);
  }, [place, position, onMove]);
  const minimap = slotRect("minimap", area);
  return (
    <div className="ot-minimap__strip" data-minimap="strip" style={{ left: logical(minimap.left), top: logical(minimap.top + minimap.height + HUD.gutter + 1), width: logical(minimap.width), height: logical(PLACE_LINE.height) }}>
      <div className="ot-minimap__place" data-minimap="place">
        <span ref={name} className="ot-minimap__place-name" data-minimap="place-name" />
        <span ref={tile} className="ot-minimap__place-tile" data-minimap="place-tile" />
      </div>
    </div>
  );
}

/**
 * The minimap in its slot and its controls beside it: zoom in, zoom out and, when the page can open the Map, the Map
 * button, which toggles it and is lit while it is open. While the Map floats (P2.12-T23, ADR-100 decision 52) or is popped
 * out (P2.12-T18, decision 27) a small mark sits on its corner and its name and hover text read "Map floating" or "Map
 * popped out".
 *
 * @param props The `area` it is laid out on and the `minimap` it reads and asks.
 * @returns The slot and its controls.
 */
export function MinimapSlot({ area, minimap }: { readonly area: HudArea; readonly minimap: HudMinimap }): ReactElement {
  const snapshot = useSyncExternalStore(minimap.source.subscribe, minimap.source.getSnapshot, minimap.source.getSnapshot);
  const button = minimap.mapButton;
  const map = button?.source ?? NO_MAP;
  const open = useSyncExternalStore(map.subscribe, () => map.getSnapshot().open, () => map.getSnapshot().open);
  /**
   * How the open Map is away from the page's own window, if it is.
   * @returns "float" or "popped", or the empty text for a Map that is shut or is the window.
   */
  const awayOf = (): "" | "float" | "popped" => {
    const now = map.getSnapshot();
    return now.open && (now.presentation === "float" || now.presentation === "popped") ? now.presentation : "";
  };
  const away = useSyncExternalStore(map.subscribe, awayOf, awayOf);
  const awayWords = away === "popped" ? say("worldMap.poppedOut") : say("worldMap.floating");
  const slot = slotRect("minimap", area);
  const { controlSize, controlGap, controlTop } = MINIMAP_LAYOUT;
  return (
    <>
      <div className="ot-hud-slot" data-hud-slot="minimap" style={{ left: logical(slot.left), top: logical(slot.top), width: logical(slot.width), height: logical(slot.height) }}>
        <HudFrame padding={0} width={slot.width} height={slot.height}>
          <div className="ot-minimap__band" data-minimap="band" style={{ width: logical(COMPASS_CANVAS), height: logical(COMPASS_CANVAS) }}>
            <CompassBand size={slot.width} scale={snapshot.hud.scale} position={minimap.position} onMove={minimap.onMove} pin={minimap.pin} party={minimap.party} places={minimap.places} placeCount={minimap.placeCount} openAt={button?.openAt} />
            <div className="ot-minimap__field" data-minimap="field" style={{ left: logical(COMPASS.band), top: logical(COMPASS.band) }}>
              <FieldCanvas field={snapshot.hud} slot="hud" position={minimap.position} onMove={minimap.onMove} ground={minimap.ground} others={minimap.others} label={say("minimap.frame")} />
            </div>
          </div>
        </HudFrame>
      </div>
      {snapshot.coordinates ? <PlaceReadout area={area} place={snapshot.place} position={minimap.position} onMove={minimap.onMove} /> : null}
      <div className="ot-minimap__controls" data-minimap="controls" style={{ left: logical(slot.left - controlSize - controlGap), top: logical(slot.top + controlTop), gap: logical(controlGap) }}>
        <ZoomButtons snapshot={snapshot} size={controlSize} minimap={minimap} />
        {button === undefined ? null : (
          <span className="ot-minimap__map" onMouseDown={keepFocus}>
            {away === "" ? (
              <GlyphButton glyph="map" label={say("worldMap.button")} size={controlSize} expanded={open} onClick={button.toggle} data={{ "data-minimap": "map" }} />
            ) : (
              <HoverWords words={awayWords}>
                <GlyphButton glyph="map" label={awayWords} size={controlSize} expanded={open} onClick={button.toggle} data={{ "data-minimap": "map" }} />
              </HoverWords>
            )}
            {away === "" ? null : <span className="ot-minimap__float-mark" data-minimap={away === "popped" ? "popout-mark" : "float-mark"} aria-hidden="true" />}
          </span>
        )}
      </div>
    </>
  );
}
